// ============================================================================
//  MAINTENANCE GUARD COMPONENT

// ============================================================================
// Wraps the app and shows maintenance screen when maintenance mode is enabled
// (Announcements are rendered by AnnouncementBanner in App.tsx.)
// 
// Usage: Wrap your App component with this:
// <MaintenanceGuard><App /></MaintenanceGuard>
// ============================================================================

import React, { useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import { auth, db } from '../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

// Routes that stay reachable during maintenance so staff can sign in and
// reach the admin dashboard to turn it back off. Non-admins who sign in are
// sent to the home page, which is blocked, so this opens nothing to players.
// /legal stays up during maintenance too: processors and regulators read it.
const MAINTENANCE_ALLOWED_PATHS = ['/login', '/admin', '/legal'];

interface MaintenanceStatus {
  enabled: boolean;
  message: string | null;
  scheduledAt: Date | null;
  estimatedDuration: number | null;
  startedAt: Date | null;
}

interface MaintenanceGuardProps {
  children: ReactNode;
}

const MaintenanceGuard: React.FC<MaintenanceGuardProps> = ({ children }) => {
  const [maintenance, setMaintenance] = useState<MaintenanceStatus | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminResolved, setAdminResolved] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const location = useLocation();

  // Check if user is admin (admins bypass maintenance). adminResolved keeps
  // the screen from flashing at an admin while the token is still loading.
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setSignedIn(!!user);
      let admin = false;
      if (user) {
        try {
          // First check custom claims
          const token = await user.getIdTokenResult();
          admin = token.claims.admin === true;
          if (!admin) {
            // Fallback: check user document role
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            admin = userDoc.exists() && userDoc.data()?.role === 'admin';
          }
        } catch {
          admin = false;
        }
      }
      setIsAdmin(admin);
      setAdminResolved(true);
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to maintenance status directly from Firestore
  useEffect(() => {
    const maintenanceRef = doc(db, 'system', 'maintenance');
    
    const unsubscribe = onSnapshot(maintenanceRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setMaintenance({
          enabled: data.enabled || false,
          message: data.message || null,
          scheduledAt: data.scheduledAt?.toDate() || null,
          estimatedDuration: data.estimatedDuration || null,
          startedAt: data.startedAt?.toDate() || null,
        });
      } else {
        setMaintenance({
          enabled: false,
          message: null,
          scheduledAt: null,
          estimatedDuration: null,
          startedAt: null,
        });
      }
    }, (error) => {
      console.error('Error subscribing to maintenance status:', error);
      // On error, assume not in maintenance - don't block users
      setMaintenance({
        enabled: false,
        message: null,
        scheduledAt: null,
        estimatedDuration: null,
        startedAt: null,
      });
    });
    
    return () => unsubscribe();
  }, []);

  // Only hold the app on the spinner when maintenance IS on and we don't yet
  // know whether this user is an admin. A normal launch renders immediately;
  // the maintenance screen swaps in as soon as the status doc arrives.
  if (maintenance?.enabled && !adminResolved) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // Show maintenance screen if maintenance mode is enabled (unless admin, or
  // on the login/admin routes staff need to switch it off)
  const onAllowedPath = MAINTENANCE_ALLOWED_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  if (maintenance?.enabled && !isAdmin && !onAllowedPath) {
    return (
      <div style={styles.maintenanceContainer}>
        <div style={styles.maintenanceCard}>
          <div style={styles.maintenanceIcon}><Wrench size={56} color="#00D9D9" /></div>

          <h1 style={styles.maintenanceTitle}>Under Maintenance</h1>
          <p style={styles.maintenanceMessage}>
            {maintenance.message || 'We are currently performing scheduled maintenance. Please check back soon.'}
          </p>
          
          {maintenance.estimatedDuration && (
            <p style={styles.maintenanceDuration}>
              Estimated duration: ~{maintenance.estimatedDuration} minutes
            </p>
          )}
          
          <div style={styles.maintenanceFooter}>
            <p style={styles.footerText}>Thank you for your patience!</p>
            <p style={styles.footerBrand}>PokerSweepStars</p>
            {signedIn ? (
              <button style={styles.staffLink} onClick={() => auth.signOut()}>Sign out</button>
            ) : (
              <a href="/login" style={styles.staffLink}>Staff sign in</a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
    </>
  );
};

// Styles
const styles: { [key: string]: React.CSSProperties } = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#0D1B2A',
  },
  spinner: {
    width: 48,
    height: 48,
    border: '4px solid rgba(0, 217, 217, 0.3)',
    borderTopColor: '#00D9D9',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: 16,
    color: '#9CA3AF',
    fontSize: 14,
  },
  maintenanceContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#0D1B2A',
    padding: 20,
  },
  maintenanceCard: {
    backgroundColor: '#1B2838',
    borderRadius: 16,
    padding: '48px 40px',
    maxWidth: 480,
    width: '100%',
    textAlign: 'center' as const,
    border: '2px solid #00D9D9',
    boxShadow: '0 0 40px rgba(0, 217, 217, 0.2)',
  },
  maintenanceIcon: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 24,
  },
  staffLink: {
    display: 'inline-block',
    marginTop: 16,
    background: 'none',
    border: 'none',
    color: '#4B5563',
    fontSize: 12,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  maintenanceTitle: {
    color: '#00D9D9',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  maintenanceMessage: {
    color: '#E5E7EB',
    fontSize: 16,
    lineHeight: 1.6,
    marginBottom: 24,
  },
  maintenanceDuration: {
    color: '#9CA3AF',
    fontSize: 14,
    marginBottom: 32,
  },
  maintenanceFooter: {
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    paddingTop: 24,
  },
  footerText: {
    color: '#6B7280',
    fontSize: 14,
    marginBottom: 8,
  },
  footerBrand: {
    color: '#D4AF37',
    fontWeight: 'bold',
    fontSize: 16,
  },
};

// Add CSS animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default MaintenanceGuard;